import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Container,
  Spinner,
  Table,
} from 'reactstrap';
import Icon from '@mdi/react';
import { mdiFileDownloadOutline, mdiFilterOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import {
  FilterDropdown,
  TableSortDisplayArrow,
  TableFilterBubbles,
  TablePagination,
} from '../../../../components/Common';
import { ExportUtil } from '../../../../utils';
import { teamPerformanceTableColumns as tableColumns } from './constants';
import { useTableHook } from '../../../../components/hooks';
import { calibrationEmployeeList } from '../../../../store/apis/calibrationService';
import eyeIcon from '../../../../assets/icons/eyeicon.png';
import { Messages } from '../../../../common/messages';
import ClearAllIcon from '../../../../assets/icons/clear-all.svg';
import './EmployeeListView.scss';
import '../../PerformanceCalibration.scss';
import { fetchCalibrationWeightageNDueDate } from '../../../../store/apis/performanceService';
import { getCurrentFinancialPeriod } from '../../../../utils/date.util';
import { getCompanyCode } from '../../../../config/axios.config';
import { showDueDate } from '../../../Performance/common';

function EmployeeListView({
  calibrationPercentageData,
  calibrationPercentageIsPending,
  toggle,
  filterData,
  resetFilterData,
}) {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const companyCode = getCompanyCode();
  const [fullRatings, setFullRatings] = useState([]);
  const [filterItem, setFilterItem] = useState({});

  const { data: performanceDueDate } = useQuery({
    queryKey: ['performanceDueDate'],
    queryFn: () =>
      fetchCalibrationWeightageNDueDate({
        type: 'duedate',
        coCd: companyCode,
        fiscalYear: getCurrentFinancialPeriod(),
      }),
  });

  const dueDateData = performanceDueDate?.result?.[0];

  const initialDefaultExternalFilters = {
    sessionId,
    status: 'List',
  };

  const removeFilterItem = (keyToReset, emptyValue) => {
    const updated = {
      ...filterItem,
      [keyToReset]: emptyValue,
    };
    setFilterItem(updated);
    resetFilterData(updated);
  };

  const clearFilter = () => {
    setFilterItem({});
    resetFilterData({});
  };

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    applyFilter,
    columnName,
    setColumnName,
    setPageLoadTrigger,
    tableDataMutation,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    nextPage,
    prevPage,
    handleSetExternalFilters,
    applyExternalFilters,
    getPageSize,
    setPageSize,
    isLoadingData,
    isLoadingColumn,
    refetch,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      ...initialDefaultExternalFilters,
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'SessionId',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: calibrationEmployeeList,
    onPageLoadTrigger: false,
  });

  useEffect(() => {
    if (
      !calibrationPercentageIsPending &&
      calibrationPercentageData?.result?.length > 0
    ) {
      refetch();
      const rawRatings = Array.from({ length: 5 }, (_, i) => {
        const rating = i + 1;
        const existing = calibrationPercentageData.result.find(
          item => Number(item?.Overall_Rating_Calibration) === rating,
        );
        return {
          Overall_Rating_Calibration: rating.toString(),
          RatingCount: existing ? existing.RatingCount : 0,
          Percentage: existing ? existing.Percentage : '0',
        };
      });

      const ratings = rawRatings.sort(
        (a, b) =>
          Number(b.Overall_Rating_Calibration) -
          Number(a.Overall_Rating_Calibration),
      );
      setFullRatings(ratings);
    } else {
      const rawRatings = Array.from({ length: 5 }, (_, i) => ({
        Overall_Rating_Calibration: (i + 1).toString(),
        RatingCount: 0,
        Percentage: '0',
      }));
      const ratings = rawRatings.sort(
        (a, b) =>
          Number(b.Overall_Rating_Calibration) -
          Number(a.Overall_Rating_Calibration),
      );
      setFullRatings(ratings);
    }
  }, [calibrationPercentageData, calibrationPercentageIsPending, refetch]);

  useEffect(() => {
    setPageLoadTrigger(true);
    setPageRequest(prev => ({
      ...prev,
    }));
  }, [setPageRequest, setPageLoadTrigger]);

  useEffect(() => {
    setFilterItem(filterData);
    handleSetExternalFilters(
      'companyCode',
      filterData?.companyCode?.Code.trim() ?? null,
    );
    handleSetExternalFilters(
      'departmentId',
      filterData?.department?.Code.trim() ?? null,
    );
    const filters = {
      companyCode: filterData?.companyCode?.Code.trim() ?? null,
      participantId: filterData?.manager?.code.trim() ?? null,
      subjectId: filterData?.employee?.code.trim() ?? null,
      departmentId: filterData?.department?.Code.trim() ?? null,
      classificationId: filterData?.classification?.value.trim() ?? null,
    };
    applyExternalFilters(filters);
  }, [filterData]);

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = data?.data;
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    const formattedData = exportResponseData?.data?.map(item => ({
      EmpName: item?.EmpName,
      Designation: item?.Designation,
      ModifiedDate: item?.ModifiedDate
        ? moment(item?.ModifiedDate).format('DD/MM/YYYY')
        : '-',
      CurrentStage: item?.CurrentStage,
      Feedback_Date: item?.Feedback_Date
        ? moment(item?.Feedback_Date).format('DD/MM/YYYY')
        : '-',
      Overall_Rating_Manager: item?.Overall_Rating_Manager
        ? Math.round(item?.Overall_Rating_Manager)
        : 'Rating not yet provided',
      Overall_Rating_Calibration: item?.Overall_Rating_Calibration
        ? Math.round(item?.Overall_Rating_Calibration)
        : 'Rating not yet provided',
    }));

    ExportUtil.downloadExcel(formattedData, {
      fileName: `Team_Performance_${timesTamp}.xlsx`,
      columns: {
        EmpName: 'Employee Name',
        Designation: 'Designation',
        ModifiedDate: 'Date',
        CurrentStage: 'Current Stage',
        Feedback_Date: 'Due Date',
        Overall_Rating_Manager: 'Overall Manager Rating',
        Overall_Rating_Calibration: 'Overall Calibration Rating',
      },
    });
  };

  const renderTableBody = () => {
    const tableData = data?.data;
    // Ensure tableData is not undefined or null

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={8}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_EMPLOYEE_FOUND
            )}
          </td>
        </tr>
      );
    }
    return tableData?.map((item, index) => (
      <tr key={index}>
        <td className="py-2">{item?.EmpName}</td>
        <td className="py-2">{item?.Designation}</td>
        <td className="py-2">
          {`${item?.ModifiedDate ? moment(item?.ModifiedDate).format('DD/MM/YYYY') : '-'}`}
        </td>
        <td className="py-2">
          {item?.CurrentStage === 'Not Started'
            ? 'Result Accepted'
            : item?.CurrentStage}
        </td>
        <td className="py-2">{showDueDate(item, dueDateData)}</td>
        <td className="py-2">
          {item?.Overall_Rating_Manager
            ? Math.round(item?.Overall_Rating_Manager)
            : 'Rating not yet provided'}
        </td>
        <td className="py-2">
          {item?.Overall_Rating_Calibration
            ? Math.round(item?.Overall_Rating_Calibration)
            : 'Rating not yet provided'}
        </td>
        <td className="py-2 text-center">
          <button
            title="View Document"
            type="button"
            className="btn btn-link p-0"
            onClick={() =>
              navigate(`/hr/performance/${item?.EmpCd}`, {
                state: { from: location.pathname, activeTab: 3 },
              })
            }
          >
            <img src={eyeIcon} alt="View" style={{ height: '15px' }} />
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <Container fluid>
      <Card className="pc-card mt-3">
        <CardBody className="p-0">
          <div className="p-3 d-flex flex-row justify-content-between align-items-center py-2">
            <div>
              <span className="fs-5">
                <strong>Team Performance</strong>
              </span>
            </div>
            <div className="d-flex gap-2">
              <div className="d-flex gap-1 align-items-center">
                {filterItem?.companyCode?.Code && (
                  <div
                    key={filterItem.companyCode.Code}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem?.companyCode?.Description}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('companyCode', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.employee?.name && (
                  <div
                    key={filterItem.employee.name}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.employee.name}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('employee', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.manager?.name && (
                  <div
                    key={filterItem.manager.name}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.manager.name}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('manager', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.department?.Description && (
                  <div
                    key={filterItem?.department?.Description}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem?.department?.Description}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('department', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.classification?.label && (
                  <div
                    key={filterItem.classification.label}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.classification.label}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('classification', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {(filterItem.companyCode ||
                  filterItem.manager ||
                  filterItem.employee ||
                  filterItem.department ||
                  filterItem.classiffication) && (
                  <div
                    className="clear-all-btn px-2"
                    onClick={() => clearFilter()}
                  >
                    <span className="clear-all-btn-text">Clear All</span>
                    <img src={ClearAllIcon} alt="Clear All" />
                  </div>
                )}
              </div>
              <Button className="btn btn-light" onClick={() => toggle()}>
                <span>Filter</span>
                <Icon path={mdiFilterOutline} size={0.8} />
              </Button>
              <Button onClick={handleExportExcel} className="btn btn-light">
                <span>Report</span>
                <Icon path={mdiFileDownloadOutline} size={0.8} />
              </Button>
            </div>
          </div>
          {!calibrationPercentageIsPending && fullRatings?.length > 0 && (
            <div className="ratings">
              {fullRatings?.map((rating, index) => (
                <div className="rating" key={index}>
                  <div className="label">
                    <div className="title">
                      Rating {rating?.Overall_Rating_Calibration}
                    </div>
                    <div className="info d-flex flex-row align-items-center gap-1">
                      <span className="value fs-4 fw-bold">
                        {rating?.RatingCount}
                      </span>
                      <span className="percent fs-5 fw-bold">
                        ({rating?.Percentage}%)
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
            />
          </div>
          <div className="table-responsive pc-table-container">
            <Table
              hover
              className="custom-table align-middle text-nowrap pc-table"
              id="performance-listing-table"
            >
              <thead>
                <tr>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2" style={{ borderRadius: '0 0 10px 10px' }}>
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </Container>
  );
}

export default EmployeeListView;
